import { useState, useEffect, useCallback } from 'react';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router-dom';
import StoreChoiceModal from '@/components/feature/StoreChoiceModal';

export default function Navbar() {
  const { t, i18n } = useTranslation();
  const [scrolled, setScrolled] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [storeModalOpen, setStoreModalOpen] = useState(false);

  const handleScroll = useCallback(() => {
    setScrolled(window.scrollY > 60);
  }, []);

  useEffect(() => {
    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [handleScroll]);

  const toggleLang = () => {
    const nextLang = i18n.language === 'fr' ? 'en' : 'fr';
    i18n.changeLanguage(nextLang);
  };

  const navLinks = [
    { key: 'nav.home', href: '/' },
    { key: 'nav.activities', href: '/#activities' },
    { key: 'nav.courts', href: '/#courts' },
    { key: 'nav.pricing', href: '/#pricing' },
    { key: 'nav.tournaments', href: '/#news' },
    { key: 'nav.advertisers', href: '/advertisers' },
    { key: 'nav.contact', href: '/contact' },
  ];

  return (
    <nav
      className={`fixed top-0 left-0 w-full z-50 transition-all duration-500 ${
        scrolled
          ? 'bg-background-50/95 backdrop-blur-md shadow-sm'
          : 'bg-transparent'
      }`}
    >
      <div className="w-full px-4 md:px-6 lg:px-10">
        <div className="flex items-center justify-between h-16 md:h-20">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-3 group">
            <div className="w-10 h-10 md:w-11 md:h-11 flex items-center justify-center rounded-full bg-primary-500 text-background-50">
              <i className="ri-trophy-line text-lg md:text-xl"></i>
            </div>
            <div className="hidden sm:block">
              <span className="font-heading text-lg md:text-xl font-semibold tracking-wide text-foreground-900">
                TENNIS CLUB
              </span>
              <span className="block font-heading text-xs md:text-sm tracking-[0.2em] uppercase text-accent-600 font-medium">
                de Douala
              </span>
            </div>
          </Link>

          {/* Desktop Nav */}
          <div className="hidden lg:flex items-center gap-1">
            {navLinks.map((link) => (
              <a
                key={link.key}
                href={link.href}
                className={`px-4 py-2 text-sm font-medium rounded-full transition-all duration-300 whitespace-nowrap cursor-pointer ${
                  scrolled
                    ? 'text-foreground-700 hover:text-primary-600 hover:bg-primary-50'
                    : 'text-background-50/90 hover:text-accent-300 hover:bg-white/10'
                }`}
              >
                {t(link.key)}
              </a>
            ))}
          </div>

          {/* Desktop CTAs */}
          <div className="hidden lg:flex items-center gap-3">
            <button
              onClick={toggleLang}
              className={`px-3 py-2 text-xs font-semibold tracking-wider uppercase rounded-full transition-all duration-300 cursor-pointer whitespace-nowrap ${
                scrolled
                  ? 'text-foreground-600 hover:text-primary-600 border border-foreground-200 hover:border-primary-300'
                  : 'text-background-50/80 hover:text-background-50 border border-background-50/30 hover:border-background-50/60'
              }`}
            >
              {t('lang.switch')}
            </button>
            <button
              onClick={() => setStoreModalOpen(true)}
              className="px-5 py-2.5 text-sm font-semibold rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 transition-all duration-300 whitespace-nowrap cursor-pointer"
            >
              {t('nav.bookCourt')}
            </button>
          </div>

          {/* Mobile Hamburger */}
          <button
            onClick={() => setMobileOpen(!mobileOpen)}
            className={`lg:hidden w-10 h-10 flex items-center justify-center rounded-full transition-all duration-300 cursor-pointer ${
              scrolled
                ? 'text-foreground-800 hover:bg-foreground-100'
                : 'text-background-50 hover:bg-white/10'
            }`}
            aria-label="Menu"
          >
            <i className={`text-xl ${mobileOpen ? 'ri-close-line' : 'ri-menu-line'}`}></i>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`lg:hidden overflow-hidden transition-all duration-400 ${
          mobileOpen ? 'max-h-[500px] opacity-100' : 'max-h-0 opacity-0'
        }`}
      >
        <div className="bg-background-50/98 backdrop-blur-md px-4 py-4 border-t border-background-200 flex flex-col gap-1">
          {navLinks.map((link) => (
            <a
              key={link.key}
              href={link.href}
              onClick={() => setMobileOpen(false)}
              className="px-4 py-3 text-sm font-medium text-foreground-700 hover:text-primary-600 hover:bg-primary-50 rounded-lg transition-all cursor-pointer"
            >
              {t(link.key)}
            </a>
          ))}
          <div className="flex items-center gap-3 pt-3 border-t border-background-200 mt-2">
            <button
              onClick={toggleLang}
              className="px-4 py-2.5 text-xs font-semibold tracking-wider uppercase rounded-full border border-foreground-200 text-foreground-600 hover:text-primary-600 hover:border-primary-300 transition-all cursor-pointer whitespace-nowrap"
            >
              {t('lang.switch')}
            </button>
            <button
              onClick={() => { setStoreModalOpen(true); setMobileOpen(false); }}
              className="px-5 py-2.5 text-sm font-semibold rounded-full bg-accent-500 text-background-50 hover:bg-accent-600 transition-all whitespace-nowrap cursor-pointer"
            >
              {t('nav.bookCourt')}
            </button>
          </div>
        </div>
      </div>

      <StoreChoiceModal open={storeModalOpen} onClose={() => setStoreModalOpen(false)} />
    </nav>
  );
}